<template>
  <div
    class="waajo-image-editor"
    role="dialog"
    aria-modal="true"
    aria-label="Editor gambar"
  >
    <div class="waajo-image-editor-dialog">
      <header class="waajo-image-editor-header">
        <div class="waajo-image-editor-title">
          <h4>Edit Gambar</h4>
          <small>{{ fileName || "Gambar lampiran" }}</small>
        </div>
        <button
          type="button"
          class="waajo-image-editor-close"
          title="Tutup editor"
          aria-label="Tutup editor"
          @click.prevent="cancel"
        >
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="waajo-image-editor-toolbar">
        <div class="waajo-image-editor-tools">
          <button
            v-for="item in tools"
            :key="item.id"
            type="button"
            class="waajo-image-editor-tool"
            :class="{ 'is-active': tool === item.id }"
            :title="item.label"
            :aria-label="item.label"
            @click.prevent="selectTool(item.id)"
          >
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </button>

          <span class="waajo-image-editor-divider"></span>

          <button
            type="button"
            class="waajo-image-editor-tool"
            title="Undo"
            aria-label="Undo"
            :disabled="!canUndo || exporting"
            @click.prevent="undo"
          >
            <i class="fa fa-undo"></i>
          </button>
          <button
            type="button"
            class="waajo-image-editor-tool"
            title="Redo"
            aria-label="Redo"
            :disabled="!canRedo || exporting"
            @click.prevent="redo"
          >
            <i class="fa fa-repeat"></i>
          </button>
          <button
            type="button"
            class="waajo-image-editor-tool"
            title="Hapus anotasi"
            aria-label="Hapus anotasi"
            :disabled="annotations.length === 0 || exporting"
            @click.prevent="clearAnnotations"
          >
            <i class="fa fa-trash-o"></i>
          </button>
        </div>

        <div class="waajo-image-editor-settings">
          <label class="waajo-image-editor-setting">
            <span>Warna</span>
            <input
              v-model="color"
              type="color"
              title="Pilih warna anotasi"
              aria-label="Pilih warna anotasi"
            />
          </label>

          <label class="waajo-image-editor-setting waajo-image-editor-stroke">
            <span>Garis {{ strokeWidth }}px</span>
            <input
              v-model.number="strokeWidth"
              type="range"
              min="1"
              max="20"
              step="1"
              aria-label="Ketebalan garis"
            />
          </label>

          <label class="waajo-image-editor-setting waajo-image-editor-text">
            <span>Teks</span>
            <input
              v-model="textValue"
              type="text"
              maxlength="160"
              placeholder="Tulis teks lalu klik gambar"
              aria-label="Teks anotasi"
              @keydown.enter.prevent
            />
          </label>
        </div>
      </div>

      <div class="waajo-image-editor-stage">
        <div v-if="loading" class="waajo-image-editor-state">
          <i class="fa fa-spinner fa-spin"></i>
          Memuat gambar...
        </div>
        <div v-else-if="loadError" class="waajo-image-editor-state is-error">
          <i class="fa fa-exclamation-circle"></i>
          {{ loadError }}
        </div>
        <div
          v-else
          class="waajo-image-editor-canvas-shell"
          :style="{ width: canvasWidth + 'px', maxWidth: '100%' }"
        >
          <canvas ref="baseCanvas" class="waajo-image-editor-base"></canvas>
          <canvas
            ref="overlayCanvas"
            class="waajo-image-editor-overlay"
            @pointerdown.prevent="onPointerDown"
            @pointermove.prevent="onPointerMove"
            @pointerup.prevent="onPointerUp"
            @pointercancel.prevent="onPointerCancel"
            @contextmenu.prevent
          ></canvas>
        </div>
      </div>

      <div v-if="notice" class="waajo-image-editor-notice">
        <i class="fa fa-info-circle"></i>
        {{ notice }}
      </div>

      <footer class="waajo-image-editor-footer">
        <span class="waajo-image-editor-hint">
          {{ toolHint }}
        </span>
        <div class="waajo-image-editor-actions">
          <button
            type="button"
            class="waajo-image-editor-btn is-secondary"
            @click.prevent="cancel"
          >
            Batal
          </button>
          <button
            type="button"
            class="waajo-image-editor-btn is-primary"
            :disabled="loading || !!loadError || exporting"
            @click.prevent="done"
          >
            <i v-if="exporting" class="fa fa-spinner fa-spin"></i>
            {{ exporting ? "Menyiapkan..." : "Selesai" }}
          </button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
const MAX_EDITOR_DIMENSION = 4096;
const MAX_HISTORY = 30;

export default {
  name: "OmniImageEditor",
  props: {
    file: {
      type: File,
      required: true
    },
    fileName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      tool: "pen",
      color: "#ff3b30",
      strokeWidth: 4,
      textValue: "",
      tools: [
        { id: "pen", label: "Pen", icon: "fa fa-pencil" },
        { id: "arrow", label: "Arrow", icon: "fa fa-long-arrow-right" },
        { id: "line", label: "Line", icon: "fa fa-minus" },
        { id: "rectangle", label: "Rectangle", icon: "fa fa-square-o" },
        { id: "circle", label: "Circle", icon: "fa fa-circle-o" },
        { id: "text", label: "Text", icon: "fa fa-font" }
      ],
      loading: true,
      loadError: "",
      exporting: false,
      notice: "",
      sourceUrl: "",
      imageElement: null,
      canvasWidth: 0,
      canvasHeight: 0,
      annotations: [],
      history: [[]],
      historyIndex: 0,
      draft: null,
      activePointerId: null
    };
  },
  computed: {
    canUndo() {
      return this.historyIndex > 0;
    },
    canRedo() {
      return this.historyIndex < this.history.length - 1;
    },
    outputMime() {
      const type = String((this.file && this.file.type) || "").toLowerCase();
      const ext = this.getExtension(this.fileName || (this.file && this.file.name));
      if (type === "image/png" || ext === "png") {
        return "image/png";
      }
      if (type === "image/webp" || ext === "webp") {
        return "image/webp";
      }
      return "image/jpeg";
    },
    outputExtension() {
      if (this.outputMime === "image/png") {
        return "png";
      }
      if (this.outputMime === "image/webp") {
        return "webp";
      }
      return "jpg";
    },
    toolHint() {
      if (this.tool === "text") {
        return "Tulis teks di toolbar, lalu klik area gambar untuk menempatkannya.";
      }
      if (this.tool === "pen") {
        return "Tekan dan geser pada gambar untuk menggambar bebas.";
      }
      return "Tekan dan geser pada gambar untuk membuat anotasi.";
    }
  },
  mounted() {
    this.loadImage();
    window.addEventListener("keydown", this.handleKeydown, false);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown, false);
    this.revokeSourceUrl();
  },
  methods: {
    getExtension(name) {
      const value = String(name || "");
      const dot = value.lastIndexOf(".");
      if (dot === -1 || dot === value.length - 1) {
        return "";
      }
      return value.slice(dot + 1).toLowerCase();
    },
    revokeSourceUrl() {
      if (this.sourceUrl) {
        try {
          (window.URL || window.webkitURL).revokeObjectURL(this.sourceUrl);
        } catch (error) {
          // Object URL mungkin sudah dilepas.
        }
        this.sourceUrl = "";
      }
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.cancel();
      }
    },
    loadImage() {
      this.loading = true;
      this.loadError = "";
      this.revokeSourceUrl();

      const URL = window.URL || window.webkitURL;
      if (!URL || !this.file) {
        this.loading = false;
        this.loadError = "Berkas gambar tidak dapat dibaca.";
        return;
      }

      try {
        this.sourceUrl = URL.createObjectURL(this.file);
      } catch (error) {
        this.loading = false;
        this.loadError = "Gagal membuat preview gambar.";
        return;
      }

      const image = new Image();
      image.onload = () => {
        this.imageElement = image;
        this.loading = false;
        this.$nextTick(() => this.setupCanvas());
      };
      image.onerror = () => {
        this.loading = false;
        this.loadError = "Gambar tidak dapat dimuat. Coba gunakan berkas lain.";
      };
      image.src = this.sourceUrl;
    },
    setupCanvas() {
      if (!this.imageElement) {
        return;
      }
      const naturalWidth = this.imageElement.naturalWidth || this.imageElement.width;
      const naturalHeight = this.imageElement.naturalHeight || this.imageElement.height;
      if (!naturalWidth || !naturalHeight) {
        this.loadError = "Ukuran gambar tidak valid.";
        return;
      }

      const scale = Math.min(
        1,
        MAX_EDITOR_DIMENSION / Math.max(naturalWidth, naturalHeight)
      );
      this.canvasWidth = Math.max(1, Math.round(naturalWidth * scale));
      this.canvasHeight = Math.max(1, Math.round(naturalHeight * scale));

      const baseCanvas = this.$refs.baseCanvas;
      const overlayCanvas = this.$refs.overlayCanvas;
      if (!baseCanvas || !overlayCanvas) {
        return;
      }

      baseCanvas.width = this.canvasWidth;
      baseCanvas.height = this.canvasHeight;
      overlayCanvas.width = this.canvasWidth;
      overlayCanvas.height = this.canvasHeight;

      const context = baseCanvas.getContext("2d");
      context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      context.drawImage(
        this.imageElement,
        0,
        0,
        this.canvasWidth,
        this.canvasHeight
      );

      this.history = [[]];
      this.historyIndex = 0;
      this.annotations = [];
      this.draft = null;
      this.redraw();
    },
    selectTool(tool) {
      this.tool = tool;
      this.notice = "";
    },
    getCanvasPoint(event) {
      const overlay = this.$refs.overlayCanvas;
      if (!overlay) {
        return { x: 0, y: 0 };
      }
      const rect = overlay.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return { x: 0, y: 0 };
      }
      return {
        x: ((event.clientX - rect.left) * overlay.width) / rect.width,
        y: ((event.clientY - rect.top) * overlay.height) / rect.height
      };
    },
    onPointerDown(event) {
      if (this.loading || this.loadError || this.exporting) {
        return;
      }
      if (typeof event.button === "number" && event.button !== 0) {
        return;
      }

      const point = this.getCanvasPoint(event);

      if (this.tool === "text") {
        if (!this.textValue.trim()) {
          this.notice = "Tulis teks terlebih dahulu sebelum menempatkannya.";
          return;
        }
        this.notice = "";
        this.addTextAnnotation(point);
        return;
      }

      this.notice = "";
      this.activePointerId =
        typeof event.pointerId === "number" ? event.pointerId : null;
      const overlay = this.$refs.overlayCanvas;
      if (
        overlay &&
        this.activePointerId !== null &&
        overlay.setPointerCapture
      ) {
        try {
          overlay.setPointerCapture(this.activePointerId);
        } catch (error) {
          // Pointer capture tidak selalu tersedia.
        }
      }

      if (this.tool === "pen") {
        this.draft = {
          type: "pen",
          color: this.color,
          width: this.strokeWidth,
          points: [point]
        };
      } else if (this.tool === "arrow" || this.tool === "line") {
        this.draft = {
          type: this.tool,
          color: this.color,
          width: this.strokeWidth,
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y
        };
      } else if (this.tool === "rectangle" || this.tool === "circle") {
        this.draft = {
          type: this.tool,
          color: this.color,
          width: this.strokeWidth,
          x1: point.x,
          y1: point.y,
          x2: point.x,
          y2: point.y
        };
      }
      this.redraw();
    },
    onPointerMove(event) {
      if (!this.draft || this.loading || this.exporting) {
        return;
      }
      if (
        this.activePointerId !== null &&
        typeof event.pointerId === "number" &&
        event.pointerId !== this.activePointerId
      ) {
        return;
      }
      const point = this.getCanvasPoint(event);
      if (this.draft.type === "pen") {
        this.draft.points.push(point);
      } else {
        this.draft.x2 = point.x;
        this.draft.y2 = point.y;
      }
      this.redraw();
    },
    onPointerUp(event) {
      if (!this.draft) {
        this.releasePointer(event);
        return;
      }
      if (
        this.activePointerId !== null &&
        typeof event.pointerId === "number" &&
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      const draft = this.draft;
      this.draft = null;
      this.releasePointer(event);

      if (draft.type === "pen") {
        if (draft.points.length > 0) {
          this.commitAnnotation(draft);
        }
      } else {
        const distance = Math.abs(draft.x2 - draft.x1) + Math.abs(draft.y2 - draft.y1);
        if (distance > 1) {
          this.commitAnnotation(draft);
        } else {
          this.redraw();
        }
      }
    },
    onPointerCancel(event) {
      this.draft = null;
      this.releasePointer(event);
      this.redraw();
    },
    releasePointer(event) {
      const overlay = this.$refs.overlayCanvas;
      if (
        overlay &&
        this.activePointerId !== null &&
        overlay.releasePointerCapture
      ) {
        try {
          overlay.releasePointerCapture(this.activePointerId);
        } catch (error) {
          // Pointer capture mungkin sudah dilepas.
        }
      }
      this.activePointerId = null;
    },
    addTextAnnotation(point) {
      const fontSize = Math.max(
        18,
        Math.min(96, Math.round(this.canvasWidth * 0.035))
      );
      this.commitAnnotation({
        type: "text",
        color: this.color,
        width: this.strokeWidth,
        x: point.x,
        y: point.y,
        text: this.textValue.trim(),
        fontSize: fontSize
      });
    },
    commitAnnotation(annotation) {
      const next = this.annotations.concat([annotation]);
      this.setAnnotations(next);
    },
    setAnnotations(nextAnnotations) {
      const next = nextAnnotations.slice();
      const history = this.history.slice(0, this.historyIndex + 1);
      history.push(next);
      while (history.length > MAX_HISTORY) {
        history.shift();
      }
      this.history = history;
      this.historyIndex = history.length - 1;
      this.annotations = next;
      this.redraw();
    },
    undo() {
      if (!this.canUndo || this.exporting) {
        return;
      }
      this.historyIndex -= 1;
      this.annotations = this.history[this.historyIndex].slice();
      this.redraw();
    },
    redo() {
      if (!this.canRedo || this.exporting) {
        return;
      }
      this.historyIndex += 1;
      this.annotations = this.history[this.historyIndex].slice();
      this.redraw();
    },
    clearAnnotations() {
      if (!this.annotations.length || this.exporting) {
        return;
      }
      this.setAnnotations([]);
    },
    redraw() {
      const overlay = this.$refs.overlayCanvas;
      if (!overlay) {
        return;
      }
      const context = overlay.getContext("2d");
      context.clearRect(0, 0, overlay.width, overlay.height);
      this.annotations.forEach(annotation => {
        this.drawAnnotation(context, annotation);
      });
      if (this.draft) {
        this.drawAnnotation(context, this.draft);
      }
    },
    applyStroke(context, annotation) {
      context.strokeStyle = annotation.color;
      context.fillStyle = annotation.color;
      context.lineWidth = annotation.width;
      context.lineCap = "round";
      context.lineJoin = "round";
    },
    drawAnnotation(context, annotation) {
      if (!annotation) {
        return;
      }
      context.save();
      this.applyStroke(context, annotation);

      if (annotation.type === "pen") {
        const points = annotation.points || [];
        if (points.length === 1) {
          context.beginPath();
          context.arc(points[0].x, points[0].y, annotation.width / 2, 0, Math.PI * 2);
          context.fill();
        } else if (points.length > 1) {
          context.beginPath();
          context.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) {
            context.lineTo(points[i].x, points[i].y);
          }
          context.stroke();
        }
      } else if (annotation.type === "line") {
        context.beginPath();
        context.moveTo(annotation.x1, annotation.y1);
        context.lineTo(annotation.x2, annotation.y2);
        context.stroke();
      } else if (annotation.type === "arrow") {
        this.drawArrow(context, annotation);
      } else if (annotation.type === "rectangle") {
        context.strokeRect(
          annotation.x1,
          annotation.y1,
          annotation.x2 - annotation.x1,
          annotation.y2 - annotation.y1
        );
      } else if (annotation.type === "circle") {
        const centerX = (annotation.x1 + annotation.x2) / 2;
        const centerY = (annotation.y1 + annotation.y2) / 2;
        const radiusX = Math.abs(annotation.x2 - annotation.x1) / 2;
        const radiusY = Math.abs(annotation.y2 - annotation.y1) / 2;
        if (radiusX > 0 && radiusY > 0) {
          context.beginPath();
          context.ellipse(
            centerX,
            centerY,
            radiusX,
            radiusY,
            0,
            0,
            Math.PI * 2
          );
          context.stroke();
        }
      } else if (annotation.type === "text") {
        context.font =
          "700 " +
          annotation.fontSize +
          'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        context.textBaseline = "top";
        context.fillText(annotation.text, annotation.x, annotation.y);
      }

      context.restore();
    },
    drawArrow(context, annotation) {
      const angle = Math.atan2(
        annotation.y2 - annotation.y1,
        annotation.x2 - annotation.x1
      );
      const headLength = Math.max(10, annotation.width * 4);
      context.beginPath();
      context.moveTo(annotation.x1, annotation.y1);
      context.lineTo(annotation.x2, annotation.y2);
      context.stroke();
      context.beginPath();
      context.moveTo(annotation.x2, annotation.y2);
      context.lineTo(
        annotation.x2 - headLength * Math.cos(angle - Math.PI / 7),
        annotation.y2 - headLength * Math.sin(angle - Math.PI / 7)
      );
      context.moveTo(annotation.x2, annotation.y2);
      context.lineTo(
        annotation.x2 - headLength * Math.cos(angle + Math.PI / 7),
        annotation.y2 - headLength * Math.sin(angle + Math.PI / 7)
      );
      context.stroke();
    },
    buildEditedFileName(mime) {
      const original = String(this.fileName || (this.file && this.file.name) || "gambar");
      const dot = original.lastIndexOf(".");
      const base = dot > 0 ? original.slice(0, dot) : original;
      let extension = this.outputExtension;
      if (mime === "image/png") {
        extension = "png";
      } else if (mime === "image/webp") {
        extension = "webp";
      } else if (mime === "image/jpeg") {
        extension = "jpg";
      }
      return (base || "gambar") + "." + extension;
    },
    canvasToBlob(canvas, mime) {
      return new Promise((resolve, reject) => {
        if (canvas.toBlob) {
          canvas.toBlob(
            blob => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Canvas tidak menghasilkan berkas."));
              }
            },
            mime,
            mime === "image/jpeg" || mime === "image/webp" ? 0.92 : undefined
          );
          return;
        }

        try {
          const dataUrl = canvas.toDataURL(mime, 0.92);
          const parts = dataUrl.split(",");
          const byteString = atob(parts[1]);
          const bytes = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            bytes[i] = byteString.charCodeAt(i);
          }
          resolve(new Blob([bytes], { type: mime }));
        } catch (error) {
          reject(error);
        }
      });
    },
    async done() {
      if (this.loading || this.loadError || this.exporting) {
        return;
      }
      if (!this.$refs.baseCanvas || !this.$refs.overlayCanvas) {
        this.notice = "Kanvas gambar belum siap. Tunggu gambar selesai dimuat.";
        return;
      }
      this.exporting = true;
      this.notice = "";
      try {
        const output = document.createElement("canvas");
        output.width = this.canvasWidth;
        output.height = this.canvasHeight;
        const context = output.getContext("2d");
        context.drawImage(this.$refs.baseCanvas, 0, 0);
        context.drawImage(this.$refs.overlayCanvas, 0, 0);
        const mime = this.outputMime;
        const blob = await this.canvasToBlob(output, mime);
        const actualMime = blob.type || mime;
        const editedFile = new File([blob], this.buildEditedFileName(actualMime), {
          type: actualMime,
          lastModified: Date.now()
        });
        this.exporting = false;
        this.$emit("done", editedFile);
      } catch (error) {
        this.exporting = false;
        this.notice =
          "Gagal menyiapkan gambar hasil edit. Coba kurangi anotasi atau gunakan gambar lain.";
      }
    },
    cancel() {
      if (this.exporting) {
        return;
      }
      this.$emit("cancel");
    }
  }
};
</script>

<style scoped>
.waajo-image-editor {
  position: fixed;
  inset: 0;
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.72);
}

.waajo-image-editor-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1180px;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.3);
}

.waajo-image-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e7edf5;
}

.waajo-image-editor-title h4 {
  margin: 0;
  color: #1d2939;
  font-size: 15px;
  font-weight: 800;
}

.waajo-image-editor-title small {
  display: block;
  overflow: hidden;
  max-width: 70vw;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waajo-image-editor-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  color: #667085;
  background: #f8fafc;
  border: 1px solid #e7edf5;
  border-radius: 8px;
  cursor: pointer;
}

.waajo-image-editor-close:hover {
  color: #b42318;
  background: #fff1f0;
  border-color: #fecaca;
}

.waajo-image-editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 16px;
  background: #fbfdff;
  border-bottom: 1px solid #e7edf5;
}

.waajo-image-editor-tools {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.waajo-image-editor-tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 30px;
  padding: 5px 7px;
  color: #475467;
  background: #ffffff;
  border: 1px solid #e7edf5;
  border-radius: 7px;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
}

.waajo-image-editor-tool:hover:not(:disabled) {
  color: #9a5d00;
  background: #fff8ec;
  border-color: #f6c26b;
}

.waajo-image-editor-tool.is-active {
  color: #ffffff;
  background: #f39c12;
  border-color: #f39c12;
}

.waajo-image-editor-tool:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.waajo-image-editor-tool .fa {
  font-size: 12px;
}

.waajo-image-editor-divider {
  width: 1px;
  height: 22px;
  margin: 0 3px;
  background: #e7edf5;
}

.waajo-image-editor-settings {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.waajo-image-editor-setting {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  color: #667085;
  font-size: 9px;
  font-weight: 700;
}

.waajo-image-editor-setting input[type="color"] {
  width: 28px;
  height: 26px;
  padding: 1px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  cursor: pointer;
}

.waajo-image-editor-stroke input[type="range"] {
  width: 78px;
}

.waajo-image-editor-text input[type="text"] {
  width: 170px;
  min-height: 28px;
  padding: 4px 7px;
  color: #344054;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 10px;
}

.waajo-image-editor-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 180px;
  padding: 14px;
  overflow: auto;
  background: #f2f4f7;
}

.waajo-image-editor-canvas-shell {
  position: relative;
  flex: 0 0 auto;
  line-height: 0;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.12);
}

.waajo-image-editor-base,
.waajo-image-editor-overlay {
  display: block;
  width: 100%;
  height: auto;
}

.waajo-image-editor-overlay {
  position: absolute;
  inset: 0;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
}

.waajo-image-editor-state {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #667085;
  font-size: 11px;
}

.waajo-image-editor-state.is-error {
  color: #b42318;
}

.waajo-image-editor-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  color: #9a5d00;
  background: #fff8ec;
  border-top: 1px solid #fde3b0;
  font-size: 10px;
}

.waajo-image-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid #e7edf5;
}

.waajo-image-editor-hint {
  color: #98a2b3;
  font-size: 9px;
  line-height: 1.4;
}

.waajo-image-editor-actions {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
}

.waajo-image-editor-btn {
  min-height: 34px;
  padding: 6px 13px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

.waajo-image-editor-btn.is-secondary {
  color: #475467;
  background: #ffffff;
  border: 1px solid #d0d5dd;
}

.waajo-image-editor-btn.is-primary {
  color: #ffffff;
  background: #f39c12;
  border: 1px solid #f39c12;
}

.waajo-image-editor-btn.is-primary:disabled {
  opacity: 0.55;
  cursor: wait;
}

@media (max-width: 767.98px) {
  .waajo-image-editor {
    padding: 0;
  }

  .waajo-image-editor-dialog {
    width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }

  .waajo-image-editor-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .waajo-image-editor-settings {
    width: 100%;
  }

  .waajo-image-editor-text {
    flex: 1 1 100%;
  }

  .waajo-image-editor-text input[type="text"] {
    flex: 1;
    width: auto;
  }

  .waajo-image-editor-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .waajo-image-editor-actions {
    width: 100%;
  }

  .waajo-image-editor-actions .waajo-image-editor-btn {
    flex: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/omni/components/ImageEditor.vue